import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Banknote,
  Building2,
  Calculator,
  ChevronDown,
  ChevronRight,
  FileSignature,
  Sparkles,
  FileText,
  Folder,
  FolderTree,
  Inbox,
  Layers,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  Users,
  X,
  CheckCircle2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TenantSwitcher } from "@/auth/TenantSwitcher";
import { EnvironmentSwitcher } from "@/auth/EnvironmentSwitcher";
import { useAuthStore } from "@/auth/store";
import { useRole } from "@/auth/RequireRole";
import { authApi, statsApi } from "@/api/endpoints";
import { cn } from "@/lib/utils";

interface NavEntry {
  to: string;
  label: string;
  icon: typeof FileText;
  end?: boolean;
  /** Wenn gesetzt: nur sichtbar wenn der User eine dieser Rollen im aktiven Tenant hat (Superuser sieht immer). */
  requireRoles?: Array<"admin" | "user" | "viewer">;
}

const navItems: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  // Postbox wird separat gerendert (s. unten)
  { to: "/todos", label: "ToDos", icon: ListChecks },
  { to: "/views", label: "Views", icon: Folder },
  { to: "/scan-environments", label: "Scanumgebungen", icon: Layers, requireRoles: ["admin", "user"] },
  { to: "/filesystem", label: "Ordnerbrowser", icon: FolderTree },
  { to: "/banking-import", label: "Banking-Import", icon: Banknote, requireRoles: ["admin", "user"] },
  { to: "/bank-suggestions", label: "KI-Assistent", icon: Sparkles, requireRoles: ["admin", "user"] },
  { to: "/auswertungen", label: "Auswertungen", icon: Calculator, requireRoles: ["admin", "user"] },
  { to: "/vertraege", label: "Verträge", icon: FileSignature, requireRoles: ["admin", "user"] },
  { to: "/stammdaten", label: "Stammdaten", icon: Building2, requireRoles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, requireRoles: ["admin", "user"] },
  { to: "/admin/users", label: "Admin", icon: Users, requireRoles: ["admin"] },
];

/**
 * Zustände der Seitenleiste auf großen Bildschirmen:
 * `full` = Symbole mit Beschriftung, `icons` = nur Symbole,
 * `hidden` = ausgeblendet, der Inhalt nutzt die ganze Breite.
 */
type SidebarMode = "full" | "icons" | "hidden";

const SIDEBAR_MODES: SidebarMode[] = ["full", "icons", "hidden"];
const SIDEBAR_STORAGE_KEY = "scantax.sidebar.mode";

function readSidebarMode(): SidebarMode {
  try {
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored && SIDEBAR_MODES.includes(stored as SidebarMode)) {
      return stored as SidebarMode;
    }
  } catch {
    // Privater Modus oder gesperrter Speicher: Standard verwenden.
  }
  return "full";
}

const SIDEBAR_LABELS: Record<SidebarMode, string> = {
  full: "Seitenleiste auf Symbole verkleinern",
  icons: "Seitenleiste ausblenden",
  hidden: "Seitenleiste einblenden",
};

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const nav = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(readSidebarMode);

  // Auswahl merken, damit sie den nächsten Besuch überdauert.
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarMode);
    } catch {
      // Nicht kritisch — die Auswahl gilt dann nur für diese Sitzung.
    }
  }, [sidebarMode]);

  const cycleSidebar = () =>
    setSidebarMode((current) => {
      const next = SIDEBAR_MODES[(SIDEBAR_MODES.indexOf(current) + 1) % 3];
      return next;
    });

  const collapsed = sidebarMode === "icons";

  // Sidebar bei Routenwechsel automatisch schließen (Mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Body-Scroll sperren, wenn das mobile Menü offen ist
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  // Escape schließt das mobile Menü
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // egal
    } finally {
      clear();
      nav("/login");
    }
  };

  /**
   * @param mini Symbol-Modus (nur auf großen Bildschirmen). Die mobile
   *   Off-Canvas-Leiste wird immer vollständig dargestellt.
   */
  const renderSidebar = (mini: boolean) => (
    <>
      <div
        className={cn(
          "border-b flex items-start justify-between gap-2",
          mini ? "px-2 py-3 flex-col items-center" : "px-4 py-5"
        )}
      >
        {!mini && (
          <div className="min-w-0">
            <div className="text-lg font-bold tracking-tight truncate">
              Scan-Intelligence.com
            </div>
            <div className="text-xs text-muted-foreground">
              Dokumenten-Management
            </div>
          </div>
        )}
        {/* Umschalter nur auf großen Bildschirmen */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden lg:inline-flex shrink-0 px-2"
          onClick={cycleSidebar}
          aria-label={SIDEBAR_LABELS[sidebarMode]}
          title={SIDEBAR_LABELS[sidebarMode]}
        >
          <PanelLeftClose className="h-5 w-5" />
        </Button>
        {/* Schließen-Button nur mobil sichtbar */}
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden shrink-0 -mr-1 px-2"
          onClick={() => setSidebarOpen(false)}
          aria-label="Menü schließen"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
      <nav
        className={cn(
          "flex-1 overflow-y-auto py-3 space-y-0.5",
          mini ? "px-1.5" : "px-2"
        )}
      >
        {/* Dashboard */}
        <NavItem item={navItems[0]} mini={mini} />
        {/* Postbox-Gruppe */}
        <PostboxNavGroup mini={mini} />
        {/* Restliche Nav-Items */}
        {navItems.slice(1).map((item) => (
          <NavItem key={item.to} item={item} mini={mini} />
        ))}
      </nav>
      {!mini && (
        <div className="px-3 py-3 border-t text-xs text-muted-foreground truncate">
          {user?.email}
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-full min-h-0">
      {/* Desktop-Sidebar (ab lg, dreistufig umschaltbar) */}
      {sidebarMode !== "hidden" && (
        <aside
          className={cn(
            "hidden lg:flex shrink-0 border-r bg-muted/30 flex-col transition-[width] duration-200",
            collapsed ? "w-16" : "w-60"
          )}
        >
          {renderSidebar(collapsed)}
        </aside>
      )}

      {/* Mobile Off-Canvas-Sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r bg-background flex flex-col shadow-xl transition-transform duration-200 ease-in-out lg:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Hauptnavigation"
      >
        {renderSidebar(false)}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 border-b flex items-center justify-between px-2 sm:px-4 gap-2 sm:gap-3">
          {/* Hamburger-Button nur mobil */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden shrink-0 px-2"
            onClick={() => setSidebarOpen(true)}
            aria-label="Menü öffnen"
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Einblenden, wenn die Seitenleiste ausgeblendet ist */}
          {sidebarMode === "hidden" && (
            <Button
              variant="ghost"
              size="sm"
              className="hidden lg:inline-flex shrink-0 px-2"
              onClick={cycleSidebar}
              aria-label={SIDEBAR_LABELS.hidden}
              title={SIDEBAR_LABELS.hidden}
            >
              <PanelLeftOpen className="h-5 w-5" />
            </Button>
          )}
          <div className="text-xs text-muted-foreground hidden md:block">
            Mandant / Umgebung:
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-1 justify-end min-w-0">
            <TenantSwitcher />
            <EnvironmentSwitcher />
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="gap-2 shrink-0"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Postbox Navigation Group (aufklappbar mit Badges)
// ---------------------------------------------------------------------------

function PostboxNavGroup({ mini = false }: { mini?: boolean }) {
  const location = useLocation();
  const [open, setOpen] = useState(() =>
    location.pathname.startsWith("/postbox") ||
    location.pathname === "/documents" ||
    location.pathname.startsWith("/documents/")
  );

  // Stats für die Badges
  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: statsApi.get,
    refetchInterval: 30_000,
  });

  const docStats = stats?.documents ?? {};
  const totalDocs = Object.values(docStats).reduce((a: number, b: number) => a + b, 0);
  const newCount = docStats["new"] ?? 0;
  const recognizedCount =
    (docStats["auto_classified"] ?? 0) +
    (docStats["confirmed"] ?? 0) +
    (docStats["filed"] ?? 0);

  const isActive =
    location.pathname.startsWith("/postbox") ||
    location.pathname === "/documents" ||
    location.pathname.startsWith("/documents/");

  // Im Symbol-Modus entfällt der Gruppenkopf: die drei Ziele stehen direkt
  // als Symbole untereinander, damit alles ohne Aufklappen erreichbar bleibt.
  if (mini) {
    const subItems = [
      { to: "/postbox/new", label: "Postbox — Neu", icon: Plus, count: newCount },
      { to: "/documents", label: "Postbox — Eingang", icon: FileText, count: totalDocs },
      {
        to: "/postbox/recognized",
        label: "Postbox — Erkannt",
        icon: CheckCircle2,
        count: recognizedCount,
      },
    ];
    return (
      <div className="space-y-0.5">
        {subItems.map(({ to, label, icon: Icon, count }) => (
          <NavLink
            key={to}
            to={to}
            title={count > 0 ? `${label} (${count})` : label}
            aria-label={label}
            className={({ isActive: active }) =>
              cn(
                "flex items-center justify-center h-11 w-full rounded-md transition-colors",
                active
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <Icon className="h-5 w-5" />
          </NavLink>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
          isActive
            ? "bg-primary/10 text-primary font-medium"
            : "hover:bg-accent hover:text-accent-foreground"
        )}
      >
        <Inbox className="h-4 w-4" />
        <span className="flex-1 text-left">Postbox</span>
        {totalDocs > 0 && (
          <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
            {totalDocs}
          </Badge>
        )}
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="ml-4 mt-0.5 space-y-0.5">
          <NavLink
            to="/postbox/new"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="flex-1">Neu</span>
            {newCount > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                {newCount}
              </Badge>
            )}
          </NavLink>
          <NavLink
            to="/documents"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <FileText className="h-3.5 w-3.5" />
            <span className="flex-1">Eingang</span>
            {totalDocs > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                {totalDocs}
              </Badge>
            )}
          </NavLink>
          <NavLink
            to="/postbox/recognized"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="flex-1">Erkannt</span>
            {totalDocs > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                {recognizedCount}/{totalDocs}
              </Badge>
            )}
          </NavLink>
        </div>
      )}
    </div>
  );
}

function NavItem({ item, mini = false }: { item: NavEntry; mini?: boolean }) {
  // useRole muss IMMER aufgerufen werden (Hook-Regel) — bei undefined dann auf true.
  const allowed = useRole(item.requireRoles ?? ["admin", "user", "viewer"]);
  if (item.requireRoles && !allowed) return null;
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={mini ? item.label : undefined}
      aria-label={mini ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-md text-sm transition-colors",
          mini ? "justify-center h-11 w-full" : "gap-2 px-3 py-2",
          isActive
            ? "bg-primary text-primary-foreground"
            : "hover:bg-accent hover:text-accent-foreground"
        )
      }
    >
      <Icon className={mini ? "h-5 w-5" : "h-4 w-4"} />
      {!mini && item.label}
    </NavLink>
  );
}

